import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

type Politeness = 'polite' | 'assertive';

interface AnnouncerContextValue {
  announce: (message: string, politeness?: Politeness) => void;
}

const AnnouncerContext = createContext<AnnouncerContextValue>({ announce: () => undefined });

/**
 * One shared aria-live region per app: autosave states, async validation
 * results, workflow events and grid counts all speak through here. Messages
 * are deduped within a short window so screen readers aren't spammed.
 */
export function LiveAnnouncerProvider({ children }: { children: ReactNode }) {
  const [politeMessage, setPoliteMessage] = useState('');
  const [assertiveMessage, setAssertiveMessage] = useState('');
  const lastRef = useRef<{ message: string; at: number }>({ message: '', at: 0 });

  const announce = useCallback((message: string, politeness: Politeness = 'polite') => {
    const now = Date.now();
    if (lastRef.current.message === message && now - lastRef.current.at < 3000) return;
    lastRef.current = { message, at: now };
    // Clear-then-set so identical consecutive strings still re-announce.
    if (politeness === 'assertive') {
      setAssertiveMessage('');
      window.setTimeout(() => setAssertiveMessage(message), 50);
    } else {
      setPoliteMessage('');
      window.setTimeout(() => setPoliteMessage(message), 50);
    }
  }, []);

  const value = useMemo(() => ({ announce }), [announce]);
  const visuallyHidden: React.CSSProperties = {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
  };

  return (
    <AnnouncerContext.Provider value={value}>
      {children}
      <div aria-live="polite" aria-atomic="true" style={visuallyHidden}>
        {politeMessage}
      </div>
      <div aria-live="assertive" role="alert" aria-atomic="true" style={visuallyHidden}>
        {assertiveMessage}
      </div>
    </AnnouncerContext.Provider>
  );
}

export function useAnnounce(): AnnouncerContextValue['announce'] {
  return useContext(AnnouncerContext).announce;
}
